import { Button, Code, Grid, Group, SimpleGrid, Text, TextInput } from '@mantine/core';
import { IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { defaultDiscount, discountError } from '../../helpers/discountConfig';
import FileUpload from '../FileUpload';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import CouponFormSection from './CouponFormSection';
import CouponPreview from './CouponsPreview';
import GeneratedCouponSettings from './GeneratedCouponSettings';

const CouponForm = ({ coupon: passedCoupon, saveCoupon, deleteCoupon, source, onBack }) => {
  const isGeneratedSource = (source || passedCoupon?.source) === 'generated';
  const [coupon, setCoupon] = useState(() =>
    isGeneratedSource && passedCoupon
      ? { ...passedCoupon, discount: { ...defaultDiscount(), ...passedCoupon.discount } }
      : passedCoupon
  );
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState();

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const navigate = useNavigate();

  const handleChange = (key, value) => {
    setCoupon({ ...coupon, [key]: value });
  };

  const isInvalidName = (name) => {
    return /[^\w]/.test(name);
  };

  if (!coupon) {
    return '';
  }

  const isFileInvalid = !isGeneratedSource && ((!coupon.id && !file) || (file && file.type !== 'text/plain'));
  const isSettingsInvalid = isGeneratedSource && Boolean(discountError(coupon.discount));

  let deleteButton = '';
  if (deleteCoupon) {
    deleteButton = (
      <LockableButton restrictionKey="couponsDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  const couponVisual = (
    <>
      <FileUpload
        onFileSet={(files) => {
          setFile(files[0]);
        }}
        isInvalid={isFileInvalid}
      />
      <Text size="sm" c="dimmed">
        We only accept plain text files (1 coupon per line)
      </Text>
      {isFileInvalid && (
        <Text size="sm" c="red">
          {file ? 'A .txt file is required' : 'A file is required'}
        </Text>
      )}
    </>
  );

  const save = (e) => {
    e.preventDefault();
    setSaving(true);
    saveCoupon(coupon, file).finally(() => setSaving(false));
  };

  const nameInput = (
    <TextInput
      name="name"
      value={coupon.name}
      error={isInvalidName(coupon.name) ? 'Please use only letters, numbers, or underscores ( _ )' : null}
      placeholder="e.g. Dynamic Coupons!"
      onChange={(e) => handleChange(e.target.name, e.target.value)}
    />
  );

  return (
    <form onSubmit={save}>
      {!isGeneratedSource && (
        <Grid align="center" mb="sm">
          <Grid.Col span={{ base: 12, sm: 2 }}>
            <Text fw={500}>Name</Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>{nameInput}</Grid.Col>
        </Grid>
      )}
      {isGeneratedSource ? (
        <GeneratedCouponSettings
          value={coupon.discount}
          onChange={(discount) => handleChange('discount', discount)}
          nameField={nameInput}
        />
      ) : (
        <Grid align="center" mb="sm">
          <Grid.Col span={{ base: 12, sm: 2 }}>
            <Text fw={500}>List of coupons</Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>{couponVisual}</Grid.Col>
        </Grid>
      )}
      {isGeneratedSource && coupon.preview?.length > 0 && (
        <CouponFormSection title="Sample codes">
          <Text size="sm" c="dimmed">
            {coupon.preview.length} unused {coupon.preview.length === 1 ? 'code' : 'codes'} from the pool. Each
            recipient gets a different code.
          </Text>
          <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="xs">
            {coupon.preview.map((code) => (
              <Code key={code}>{code}</Code>
            ))}
          </SimpleGrid>
        </CouponFormSection>
      )}
      {!isGeneratedSource && (file || coupon.preview) && (
        <Grid align="center" mb="sm">
          <Grid.Col span={{ base: 12, sm: 2 }}></Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <CouponPreview file={file} previewList={!file ? coupon.preview : null} />
          </Grid.Col>
        </Grid>
      )}
      <Group justify="flex-end" gap="xs" py="md">
        <Button variant="default" onClick={() => (onBack ? onBack() : navigate(-1))}>
          Back
        </Button>
        {deleteButton}
        <LockableButton
          restrictionKey={`coupons${coupon.id ? 'Update' : 'Create'}`}
          type="submit"
          disabled={!coupon.name || isInvalidName(coupon.name) || saving || isFileInvalid || isSettingsInvalid}
        >
          <IconDeviceFloppy size={14} /> {saving ? 'Saving...' : 'Save'}
        </LockableButton>
        <ConfirmationModal
          show={confirmDeleteOpen}
          close={() => setConfirmDeleteOpen(false)}
          confirm={(cb) => deleteCoupon(cb)}
          message="This action cannot be undone!"
        />
      </Group>
    </form>
  );
};

export default CouponForm;
